import { useMemo } from 'react';

import { usePageMenuNames } from 'hb-react/shared/hooks/usePageMenuNames';
import {
  type ENTITY_TYPES,
  ENTITY_PAGES,
  ENTITY_TYPES_PER_PAGE,
} from 'hb-react/shared/utils/entitiesContentData/constants';
import { type EntityType } from 'hb-react/shared/utils/entitiesContentData/types';

import { getPageIdentifiersForEntityTypes } from '../utils/entityPages';

type ContentTypeNamesMap = {
  [key in keyof typeof ENTITY_TYPES]?: string;
};

/**
 * Hook that maps entity types to their corresponding custom page names.
 *
 * Takes the custom page names configured for the network and creates
 * a mapping where entity types (like 'opportunity', 'company', etc.)
 * are mapped to their localized page names.
 *
 * @param opts {Object} Options object.
 * @param [opts.entityTypes] {Array<EntityType>} The entity types to map to page names.
 * @returns A map of entity type identifiers to their display names
 * @example
 * const contentTypeNames = useContentTypeNames();
 * // { 'opportunity': 'Opportunities', 'company': 'Companies', ... }
 */
export const useContentTypeNames = ({
  entityTypes,
}: { entityTypes?: Array<EntityType> } = {}): ContentTypeNamesMap => {
  const requestedPageIdentifiers = entityTypes
    ? getPageIdentifiersForEntityTypes(entityTypes)
    : ENTITY_PAGES;

  // Returns a hash of page identifiers and custom page names
  const pageNames = usePageMenuNames(requestedPageIdentifiers);

  return useMemo(
    () =>
      Object.entries(pageNames).reduce<ContentTypeNamesMap>(
        (acc, [pageIdentifier, pageName]) => {
          const entityTypesList = ENTITY_TYPES_PER_PAGE[pageIdentifier];

          entityTypesList?.forEach((entityType) => {
            // If page name is fetching, it will be an empty string, or
            // it's default name, or undefined if such name is not set in
            // the default names.
            acc[entityType] = pageName;
          });

          return acc;
        },
        {},
      ),
    [pageNames],
  );
};
